import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Typography, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import Hls from "hls.js";


import LoadingProgressBar from "../../common/LoadingProgressBar";


const VideoPlayer = ({ videoSrc, onClose, title }) => {
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let hls;

    const initializePlayer = () => {
      setError(false); // Reset error state
      if (videoSrc.endsWith(".m3u8")) {
        if (Hls.isSupported()) {
          hls = new Hls();
          hls.loadSource(videoSrc);
          hls.attachMedia(videoRef.current);

          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            setLoading(false);
            videoRef.current
              .play()
              .catch((err) => console.warn("Error playing video:", err));
          });

          hls.on(Hls.Events.ERROR, (event, data) => {
            if (data.fatal) {
              setError(true);
              hls.destroy();
            }
          });
        } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
          videoRef.current.src = videoSrc;
          videoRef.current.addEventListener("loadeddata", () => {
            setLoading(false);
            videoRef.current
              .play()
              .catch((err) => console.warn("Error playing video:", err));
          });
        } else {
          console.error("HLS not supported on this browser.");
          setError(true);
        }
      } else if (videoSrc.endsWith(".mp4")) {
        videoRef.current.src = videoSrc;
        videoRef.current.addEventListener("loadeddata", () => {
          setLoading(false);
          videoRef.current
            .play()
            .catch((err) => console.warn("Error playing video:", err));
        });
      } else {
        console.error("Unsupported video format.");
        setError(true);
      }
    };

    initializePlayer();

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [videoSrc]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current.requestFullscreen) {
      videoRef.current.requestFullscreen();
    } else {
      console.warn("Fullscreen API is not supported by this browser.");
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        backgroundColor: "#000",
      }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {loading && !error && (
       <LoadingProgressBar />
      )}
      {error && (
        <Typography
          variant="body1"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            zIndex: 10,
          }}
        >
          Unable to load video. Please try again later.
        </Typography>
      )}
      <video
        ref={videoRef}
        controls
        style={{
          width: "100%",
          height: "100%",
          display: loading || error ? "none" : "block",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "80px",
          background:
            "linear-gradient(to bottom, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0))",
          zIndex: 5,
        }}
      />
      <Typography
        variant="body1"
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          color: "#fff",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          padding: "8px 16px",
          borderRadius: 2,
          zIndex: 5,
        }}
      >
        {title}
      </Typography>
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 5,
          color: "#fff",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          },
        }}
      >
        <CloseIcon />
      </IconButton>
      <Tooltip title="Fullscreen">
        <IconButton
          onClick={handleFullscreen}
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            zIndex: 5,
            color: "#fff",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
        >
          <FullscreenIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default VideoPlayer;